import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';

import SearchBar from '../../../components/search-bar/SearchBar';
import useDialogContext from '../../../hooks/dialog/useDialogContext';
import { useComponentsTableQueryParams } from '../../../hooks/useComponentsTableQueryParams';
import LocationFilter from '../../../shared/components/location-filter/LocationFilter';
import StatusFilter from '../../../shared/components/status-filter/StatusFilter';
import { PCFBiogenicEmissionsMode } from '../../../shared/enums/pcf';
import { PCFRequestStatus } from '../../../shared/enums/pcf-request';
import { serializeMulti } from '../../../shared/helpers/url-parameter-parser';
import { IComponent } from '../../../shared/interfaces/IComponent';
import BulkComponentActionsButton from './BulkComponentActionsButton';
import styles from './ComponentsHeader.module.css';

const ALLOWED_COMPONENT_STATUSES = [
  PCFRequestStatus.Open,
  PCFRequestStatus.Outdated,
  PCFRequestStatus.Complete,
  PCFRequestStatus.Updated,
];

type ComponentsHeaderProps = {
  biogenicEmissionsMode: PCFBiogenicEmissionsMode;
  setBiogenicEmissionsMode: (mode: PCFBiogenicEmissionsMode) => void;
  componentsToShowPendingDuringBulk: IComponent[];
  setComponentsToShowPendingDuringBulk: (products: IComponent[]) => void;
};

/* Only commenting this part as per PAC-1292 */
/* const getTranslatedEmissionsRemovals = (t: TFunction<'translation', undefined>) => {
  const options = Object.values(PCFBiogenicEmissionsMode);

  return options.map((mode) => {
    return {
      label: t(`biogenicEmissionsMode.${mode.toLowerCase()}`),
      value: mode,
    };
  });
}; */

const ComponentsHeader = ({
  biogenicEmissionsMode,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setBiogenicEmissionsMode,
  componentsToShowPendingDuringBulk,
  setComponentsToShowPendingDuringBulk,
}: ComponentsHeaderProps): JSX.Element => {
  const { t } = useTranslation();

  const { openDialog } = useDialogContext();
  const { filters, setParams } = useComponentsTableQueryParams();

  const onSearchChange = (value: string) => {
    setParams({ search: value, page: 1 });
  };

  const onStatusFilterChange = (selectedStatuses: string[]) => {
    setParams({
      status: serializeMulti(selectedStatuses),
      page: 1,
    });
  };

  const onLocationsFilterChange = (countries: string[], regions: string[]) => {
    setParams({
      country: serializeMulti(countries),
      region: serializeMulti(regions),
      page: 1,
    });
  };

  return (
    <div className='flex flex-column lg:flex-wrap lg:flex-row w-full align-items-center justify-content-start gap-2 lg:justify-content-between'>
      <div
        className={`flex flex-column md:flex-wrap md:flex-row gap-2 w-full md:w-auto ${styles['header-buttons']}`}
      >
        {/* Only commenting this part as per PAC-1292 */}
        {/* <span>{t('componentsPage.biogenicEmissionsRemovals')}</span>
          <SelectButton
            className='flex'
            value={biogenicEmissionsMode}
            onChange={(e: SelectButtonChangeEvent) => {
              setBiogenicEmissionsMode(e.value);
            }}
            options={getTranslatedEmissionsRemovals(t)}
            unselectable={false}
          /> */}
        <Button
          label={t('componentsPage.requestPcfForAdditionalComponent')}
          onClick={() => {
            openDialog('request-additional-component-dialog');
          }}
        />
        <BulkComponentActionsButton
          biogenicEmissionsMode={biogenicEmissionsMode}
          componentsToShowPendingDuringBulk={componentsToShowPendingDuringBulk}
          setComponentsToShowPendingDuringBulk={setComponentsToShowPendingDuringBulk}
        />
      </div>

      <div
        className={`flex flex-column md:flex-wrap md:flex-row gap-2 align-items-center w-full md:w-auto ${styles['header-filters']}`}
      >
        <label className='hidden md:block'>{t('componentsPage.filters')} </label>

        <div className='w-full flex-1'>
          <LocationFilter onLocationsFilterChange={onLocationsFilterChange} filters={filters} />
        </div>

        <div className='w-full flex-1'>
          <StatusFilter
            onStatusFilterChange={onStatusFilterChange}
            filters={filters}
            allowedStatuses={ALLOWED_COMPONENT_STATUSES}
          />
        </div>

        <div className='w-full flex-1'>
          <SearchBar onSearchChange={onSearchChange} initialValue={filters.search} />
        </div>
      </div>
    </div>
  );
};

export default ComponentsHeader;
